// apps/web/src/widgets/measurement/format-amount.ts
//
// Phase 11.8 REND-01 (Strand 2) — the pure named-amount formatter. It applies a
// recipe-DECLARED measurement vocabulary table + a decomposition ladder to a raw
// subdivision count, producing a PRIMARY amount string plus a set of
// material-difference-gated alternates. The engine holds ONLY the algorithm shape
// (a largest→smallest fold over the ladder + the four alternate sources); every
// unit NAME, denominator, named-set membership, and alternate-ladder is DERIVED
// from the DATA params (the `vocab.tokens` + `vocab.ladder` the recipe supplies).
// This module contains ZERO unit-name literals — it is units-OPAQUE (the C1
// generality contract; the engine never branches on a literal unit name).
//
// Consumed by BOTH the Strand-1 shopping-list renderer (Plan 04) and the
// per-option-detail-card print wiring (Plan 05). It is NOT a template, NOT a
// renderer, has NO WidgetState — a pure function set mirroring the purity
// discipline of size-and-yield/format-yield.ts EXACTLY.
//
// PURITY (mirrors format-yield.ts): no observable side effects. There is NO
// console.* on degenerate inputs — per review finding WR-04 a console.error on
// expected-degenerate inputs violates the "pure helper" contract, pollutes
// user-facing developer consoles, and makes tests brittle (spy on console.error
// instead of assert on a return value). The documented return value is the SOLE
// observable signal: a non-finite input returns `undefined` and the renderer owns
// the "—" fallback; a non-positive input returns the "0"/empty-alts result. If a
// richer error channel is needed later, prefer widening the return type to a
// discriminated union rather than re-introducing a console side effect. NO Date,
// NO Math.random, NO DOM, NO React import — a plain function testable without any
// DOM.
//
// DoS defense (T-11.8-DoS-decomp): the fold is bounded by a per-tier
// Math.max(0, Math.floor(...)) clamp and a finite tier list — it cannot loop on a
// pathological count. A non-finite count is rejected at entry; a non-positive
// count short-circuits to "0".
import type { MeasurementVocabulary } from "@the-recipe-spread/shared";

const EPS = 1e-9;

/** The screen/print-ready result. NOT a pre-joined string: the renderer maps
 *  `alts` to inline notes (screen) or joins them with "; " in one parenthetical
 *  (print). The material-difference dedupe happens INSIDE the formatter (a `seen`
 *  Set), so `alts` holds only material-different alternates. */
export interface FormattedAmount {
  readonly primary: string;
  readonly alts: ReadonlyArray<string>;
}

type LadderTier = MeasurementVocabulary["ladder"][number];
type Token = MeasurementVocabulary["tokens"][number];

// Vulgar-fraction glyphs (generic math glyphs — NOT unit names). Used to render a
// fractional tier count (e.g. a whole + a half) and a sub-finest-tier fraction.
const VULGAR: ReadonlyArray<readonly [number, string]> = [
  [1 / 8, "⅛"],
  [1 / 4, "¼"],
  [3 / 8, "⅜"],
  [1 / 2, "½"],
  [5 / 8, "⅝"],
  [2 / 3, "⅔"],
  [3 / 4, "¾"],
  [7 / 8, "⅞"],
  [1 / 3, "⅓"],
];

// A label is a "named" token (eligible for the named-amount + name-swap +
// fractional-decomposition machinery) when it is NOT one of the ladder's tier
// labels AND does not begin with a digit or a vulgar-fraction glyph (those are the
// plain fractional-of-finest-tier rows). DERIVED from DATA — there is no hardcoded
// named set.
const STARTS_WITH_FRACTION_OR_DIGIT = /^[0-9¼½¾⅐⅑⅒⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/;

function vulgarGlyph(frac: number): string | null {
  for (const [value, glyph] of VULGAR) {
    if (Math.abs(value - frac) < EPS) return glyph;
  }
  return null;
}

interface Derived {
  readonly ladder: ReadonlyArray<LadderTier>;
  readonly finestTier: LadderTier; // the tier with granularity "any" (absorbs the residual)
  readonly finestNum: number;
  readonly finestIdx: number;
  readonly named: ReadonlyArray<Token>;
  readonly coarseFracNum: number | null; // the first multi-fraction tier above the finest tier (≥3 granularity options) — the coarse-fraction gate unit
}

/** Build the DATA-derived constants the fold needs. Units stay opaque: every
 *  denominator/label/named-set entry comes from the supplied vocabulary. */
function derive(vocab: MeasurementVocabulary): Derived | null {
  const ladder = vocab.ladder;
  if (ladder.length === 0) return null;

  const finestIdx = ladder.findIndex((t) => t.granularity === "any");
  // The ladder must declare exactly one finest tier (granularity "any") and it
  // must be last — the fold's residual-absorbing invariant. If the recipe ladder
  // is malformed, fail closed (the caller's renderer owns the "—" fallback).
  if (finestIdx !== ladder.length - 1) return null;

  const finestTier = ladder[finestIdx];
  if (finestTier === undefined) return null;
  const finestNum = finestTier.num;
  if (!Number.isFinite(finestNum) || finestNum <= 0) return null;

  const tierLabels = new Set(ladder.map((t) => t.label.toLowerCase()));
  const named = vocab.tokens.filter(
    (t) => !tierLabels.has(t.label.toLowerCase()) && !STARTS_WITH_FRACTION_OR_DIGIT.test(t.label),
  );

  let coarseFracNum: number | null = null;
  for (let i = 0; i < finestIdx; i++) {
    const tier = ladder[i];
    if (tier === undefined) continue;
    const g = tier.granularity;
    if (Array.isArray(g) && g.length >= 3) {
      coarseFracNum = tier.num;
      break;
    }
  }

  return { ladder, finestTier, finestNum, finestIdx, named, coarseFracNum };
}

function namedAt(d: Derived, num: number): Token | undefined {
  return d.named.find((t) => t.num === num);
}

function namedSibling(d: Derived, num: number, label: string): Token | undefined {
  return d.named.find((t) => t.num === num && t.label !== label);
}

/** Standalone (sole token) → the label as authored + parenthetical fraction.
 *  Continuation (after a "+") → the lowercase a-prefixed form. The rule is
 *  positional and generic — it never hardcodes a specific name. */
function nameForPosition(
  d: Derived,
  label: string,
  fracGlyph: string | null,
  isContinuation: boolean,
): string {
  const paren = fracGlyph ? ` (${fracGlyph} ${d.finestTier.label})` : "";
  return isContinuation ? `a ${label.toLowerCase()}${paren}` : `${label}${paren}`;
}

/** Render a finest-tier count (which may carry a fraction) as "N⅞ <tier>". */
function renderFinestCount(d: Derived, count: number): string {
  const whole = Math.floor(count + EPS);
  const frac = count - whole;
  const glyph = frac > EPS ? vulgarGlyph(frac) : null;
  let numStr: string;
  if (whole > 0 && glyph) numStr = `${whole}${glyph}`;
  else if (whole > 0) numStr = `${whole}`;
  else if (glyph) numStr = glyph;
  else numStr = "0";
  return `${numStr} ${d.finestTier.label}`;
}

/** The finest tier absorbs the entire residual. If the residual is a sub-tier
 *  named token, emit its standalone/continuation name; otherwise render the
 *  whole+fraction count. */
function formatFinestTier(
  d: Derived,
  residual: number,
  isContinuation: boolean,
  nameLabelOverride: string | undefined,
): string {
  if (residual <= 0) return "";
  const count = residual / d.finestNum;
  if (nameLabelOverride !== undefined) {
    const glyph = vulgarGlyph(count);
    return nameForPosition(d, nameLabelOverride, glyph, isContinuation);
  }
  const named = residual < d.finestNum ? namedAt(d, residual) : undefined;
  if (named) {
    const glyph = vulgarGlyph(count);
    return nameForPosition(d, named.label, glyph, isContinuation);
  }
  return renderFinestCount(d, count);
}

type TierKind = "whole-only" | "fractional" | "half-tier";

/** Classify a non-finest tier by the SHAPE of its DATA (granularity), never by
 *  name: a whole-only tier (granularity [1]) carries the "big" residue gate; the
 *  tier immediately above the finest tier carries the bare-fraction-suppression
 *  gate; any other fractional tier carries the bare-fraction-if-residue gate. */
function classify(tier: LadderTier, index: number, finestIdx: number): TierKind {
  const g = tier.granularity;
  if (Array.isArray(g) && g.length === 1 && Math.abs(g[0] - 1) < EPS) return "whole-only";
  if (index === finestIdx - 1) return "half-tier";
  return "fractional";
}

/** The largest→smallest fold. Returns the joined primary/alt string. The optional
 *  `tiers` override drives the tier-set alternates (Alt-1/Alt-2 derived ladders);
 *  the optional `nameLabelOverride` drives the name-swap alternate. */
function decompose(
  d: Derived,
  tspNum: number,
  tiers: ReadonlyArray<LadderTier>,
  nameLabelOverride: string | undefined,
): string {
  let r = tspNum;
  const segments: string[] = [];

  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (tier === undefined) continue;

    if (tier.granularity === "any") {
      const isContinuation = segments.length > 0;
      const segment = formatFinestTier(d, r, isContinuation, nameLabelOverride);
      if (segment) segments.push(segment);
      r = 0;
      break;
    }

    const num = tier.num;
    // DoS clamp: a non-finite or pathological count cannot loop or go negative.
    const whole = num > 0 ? Math.max(0, Math.floor(r / num)) : 0;
    const afterWhole = r - whole * num;

    // Largest cumulative sub-1 granularity that fits the post-whole remainder.
    const fractions = tier.granularity.filter((x) => x < 1).sort((a, b) => b - a);
    let fracSum = 0;
    for (const x of fractions) {
      if ((fracSum + x) * num <= afterWhole + EPS) fracSum += x;
    }

    const kind = classify(tier, i, d.finestIdx);
    let emitWhole = whole;
    let emitFrac = fracSum;

    if (kind === "whole-only") {
      // Fire only when whole ≥ 1 AND the post-residue is 0 or ≥ one coarse-fraction
      // unit (prevents promoting a part-tier amount into a larger unit + a stray
      // remainder). Whole-only tiers never emit a fraction.
      const residueAfter = r - whole * num;
      const fires =
        whole >= 1 &&
        (residueAfter === 0 || (d.coarseFracNum != null && residueAfter >= d.coarseFracNum));
      emitWhole = fires ? whole : 0;
      emitFrac = 0;
    } else if (kind === "fractional") {
      // A bare fraction is allowed when a whole exists OR the bare-fraction emit
      // leaves a residue ≥ the next tier (so the fraction is worth surfacing).
      const nextNum = tiers[i + 1]?.num ?? 0;
      const residueAfterFrac = r - whole * num - fracSum * num;
      const fires = whole >= 1 || (fracSum > 0 && residueAfterFrac >= nextNum);
      if (!fires) {
        emitWhole = 0;
        emitFrac = 0;
      }
    } else {
      // half-tier: a BARE fraction (no whole) is SUPPRESSED so the finest tier
      // re-expresses it (the canonical "sugar-bug" fix). A whole keeps its
      // fraction.
      if (whole === 0 && fracSum > 0) {
        emitWhole = 0;
        emitFrac = 0;
      }
    }

    if (emitWhole + emitFrac > 0) {
      const glyph = emitFrac > 0 ? vulgarGlyph(emitFrac) : null;
      let numStr: string;
      if (emitWhole > 0 && glyph) numStr = `${emitWhole}${glyph}`;
      else if (emitWhole > 0) numStr = `${emitWhole}`;
      else if (glyph) numStr = glyph;
      else numStr = `${emitWhole}`;
      segments.push(`${numStr} ${tier.label}`);
      r = r - emitWhole * num - emitFrac * num;
    }
  }

  return segments.join(" + ");
}

/** Alt-1: drop the bare fraction from the half-tier (the tier above the finest
 *  tier) by collapsing its granularity to whole-only. DERIVED from the primary
 *  ladder. */
function deriveAlt1(d: Derived): ReadonlyArray<LadderTier> {
  return d.ladder.map((t, i) =>
    i === d.finestIdx - 1 && Array.isArray(t.granularity) ? { ...t, granularity: [1] } : t,
  );
}

/** Alt-2: Alt-1 with the coarse-fraction tier's finest fraction dropped (keep
 *  whole + half only). DERIVED from the primary ladder. */
function deriveAlt2(d: Derived): ReadonlyArray<LadderTier> {
  return deriveAlt1(d).map((t) => {
    const g = t.granularity;
    if (Array.isArray(g) && g.length >= 3) {
      const kept = g.filter((x) => Math.abs(x - 1) < EPS || Math.abs(x - 0.5) < EPS);
      return { ...t, granularity: kept };
    }
    return t;
  });
}

/** Split a "<Prefix> <Base>" named label into its prefix + base. */
function splitPrefix(label: string): { prefix: string; base: string } | null {
  const sp = label.indexOf(" ");
  if (sp < 0) return null;
  return { prefix: label.slice(0, sp), base: label.slice(sp + 1) };
}

/** The fractional-decomposition alternate: a prefix token re-expressed as a sum of
 *  smaller tokens. The component multipliers are DERIVED from the ratio of the
 *  token to its base (1.5 → base + base/2; 0.75 → base/2 + base/4) — never keyed
 *  on the literal prefix word. */
function fractionalDecomp(d: Derived, num: number): string | null {
  const token = namedAt(d, num);
  if (!token) return null;
  const parts = splitPrefix(token.label);
  if (!parts) return null;
  const base = d.named.find((t) => t.label === parts.base);
  if (!base || base.num <= 0) return null;

  const ratio = num / base.num;
  let mults: ReadonlyArray<number> | null = null;
  if (Math.abs(ratio - 1.5) < EPS) mults = [1, 0.5];
  else if (Math.abs(ratio - 0.75) < EPS) mults = [0.5, 0.25];
  if (mults === null) return null;

  const segments = mults.map((m) => {
    const componentNum = base.num * m;
    const component = namedAt(d, componentNum);
    if (component) return component.label;
    const count = componentNum / d.finestNum;
    const glyph = vulgarGlyph(count);
    return glyph ? `${glyph} ${d.finestTier.label}` : `${count} ${d.finestTier.label}`;
  });
  return segments.join(" + ");
}

/**
 * Format a raw subdivision count `tspNum` against a recipe-DECLARED measurement
 * vocabulary + decomposition ladder, producing a primary amount string plus the
 * ordered, material-difference-gated alternates.
 *
 * - Non-finite (NaN / ±Infinity) → `undefined` (the renderer owns the "—"
 *   fallback).
 * - `tspNum ≤ 0` → `{ primary: "0", alts: [] }`.
 * - Otherwise → `{ primary, alts }` where `alts` carries (in order) the tier-set
 *   Alt-1, tier-set Alt-2, the name-swap sibling, and the fractional
 *   decomposition — each emitted only when materially different from the primary
 *   and from the alternates already emitted (dedupe via a `seen` Set).
 */
export function formatAmount(
  tspNum: number,
  vocab: MeasurementVocabulary,
): FormattedAmount | undefined {
  // Non-finite guard at entry — the sole failure signal is the `undefined` return.
  if (!Number.isFinite(tspNum)) {
    return undefined;
  }
  // A negative NON-integer is garbage (a count is an integer multiple of the
  // finest subdivision) → undefined; the renderer owns the "—" fallback. A clean
  // non-positive integer (0 or a negative whole count) clamps to the bounded "0"
  // result — mirroring the reference `Math.max(0, Math.floor(...))` clamp — and
  // never folds or loops.
  if (tspNum < 0 && !Number.isInteger(tspNum)) {
    return undefined;
  }
  if (tspNum <= 0) {
    return { primary: "0", alts: [] };
  }

  const d = derive(vocab);
  if (d === null) {
    // A malformed ladder is treated as a degenerate input (renderer owns "—").
    return undefined;
  }

  const primary = decompose(d, tspNum, d.ladder, undefined);

  const seen = new Set<string>([primary]);
  const alts: string[] = [];
  const push = (candidate: string | null): void => {
    if (candidate && !seen.has(candidate)) {
      seen.add(candidate);
      alts.push(candidate);
    }
  };

  // Source 1 + 2: the tier-set alternates (derived ladders).
  push(decompose(d, tspNum, deriveAlt1(d), undefined));
  push(decompose(d, tspNum, deriveAlt2(d), undefined));

  // Source 3 + 4: the name-swap sibling + the fractional decomposition. These apply
  // when the residual is a sub-finest-tier named token (the collision pairs).
  if (tspNum < d.finestNum) {
    const primaryNamed = namedAt(d, tspNum);
    if (primaryNamed) {
      const sibling = namedSibling(d, tspNum, primaryNamed.label);
      if (sibling) push(decompose(d, tspNum, d.ladder, sibling.label));
      push(fractionalDecomp(d, tspNum));
    }
  }

  return { primary, alts };
}
